/**
 * PassiveTickers — фоновые таймеры для апгрейдов doc 27 §1, §3.
 *
 * 1. **autoTap** (b011 Помощник тишины) — каждые N секунд имитирует
 *    тап от лица игрока. Использует обычный `tap()` action store
 *    (получает осознания, увеличивает стрик, может триггерить активации).
 *    Лорно: Проводник касается Искры пока ты в покое.
 *
 * 2. **passiveAwareness** (u025 Тихое дыхание) — каждую секунду
 *    начисляет N осознаний напрямую (через `grantAtlasReward` reuse).
 *    Лорно: Искра дышит вместе с тобой.
 *
 * Wire: mount в App.tsx один раз. Renders null.
 *
 * Edge cases:
 *   - Активация Сай открыта → autoTap пропускает (не дёргать через overlay)
 *   - reveal overlay активен → autoTap пропускает (не блокировать чтение)
 *   - Эпоха ещё < unlock epoch — нет апгрейдов → no-op (interval=0)
 *   - Пользователь развернул другую вкладку браузера — setInterval throttle'нется
 *     автоматически (browser-native), это даёт rough offline-progress effect
 */
import { useEffect } from 'react';
import { UPGRADES_ALPHA } from '@sai-clicker/content';
import { computeAutoTapIntervalMs, computePassiveAwarenessPerSec } from '@sai-clicker/game-core';
import { useGameStore } from '../stores/game.js';

/** Пассив-tickers работают по «нормальной» секунде. */
const PASSIVE_TICK_MS = 1000;

export function PassiveTickers(): null {
  const purchasedUpgrades = useGameStore((s) => s.state.purchasedUpgrades);
  const activeActivation = useGameStore((s) => s.activeActivation);

  // --- 1. AutoTap ---
  // Считаем интервал из покупок. Если 0 — нет авто-тапа, useEffect cleanups.
  const autoTapInterval = computeAutoTapIntervalMs(purchasedUpgrades, UPGRADES_ALPHA);

  useEffect(() => {
    if (autoTapInterval === 0) return undefined;
    const id = window.setInterval(() => {
      // Skip auto-tap во время активации Сай — не мешаем чтению overlay'я.
      const state = useGameStore.getState();
      if (state.activeActivation !== null) return;
      // Tap «по центру» — phrase появится где-то выше Искры через
      // FloatingPhrasesLayer как обычно. Координаты 0.5/0.4 = центр-верх.
      state.tap(0.5, 0.4);
    }, autoTapInterval);
    return () => {
      window.clearInterval(id);
    };
    // activeActivation НЕ в deps — мы проверяем внутри tick'а, иначе
    // interval бы пересоздавался на каждое открытие/закрытие активации.
  }, [autoTapInterval]);

  // --- 2. Passive awareness ---
  const passivePerSec = computePassiveAwarenessPerSec(purchasedUpgrades, UPGRADES_ALPHA);

  useEffect(() => {
    if (passivePerSec === 0) return undefined;
    const id = window.setInterval(() => {
      const state = useGameStore.getState();
      // Even during activations — passive должен продолжаться, это
      // фоновый ambient процесс. Просто начисляем awareness напрямую.
      state.grantAtlasReward(passivePerSec, 0);
    }, PASSIVE_TICK_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [passivePerSec]);

  // Подавляем lint warning о неиспользуемой переменной (она нужна для
  // подписки на store, чтобы re-render'ить и пересчитать intervals).
  void activeActivation;

  return null;
}
